import { useEffect, useState } from "react";
import { Button, Input, Modal, Popconfirm, Space, Table, TableColumnsType, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import Api from "../../api/Api";
import FirmwareApi from "../../api/FirmwareApi";
import CreateFirmwareWizard from "./components/create-firmware/CreateFirmwareWizard";
import StringUtils from "../../util/StringUtils";
import { FirmwareManagementWrapper, TableWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper } from "./FirmwareManagement.styled";

interface FirmwareManagement {

}

interface ProductFirmwareView {
    productModel: string;
    latestVersion: string;
    deviceCount: number;
    firmwares: FirmwareView[];
}

interface FirmwareView {
    id: string,
    productModel: string;
    version: string;
    remarks: string;
    size: number;
    md5: string;
    deviceCount: number;
}

const FirmwareManagement: React.FC<FirmwareManagement> = (props)=> {

    // api
    let firmwareApi = new FirmwareApi();
    const [messageApi, contextHolder] = message.useMessage();
    
    // state
    let [loading, setLoading] = useState(true);
    let [openCreateFirmwareModal, setOpenCreateFirmwareModal] = useState(false);
    let [firmwareViews, setFirmwareViews] = useState([]);
    let [editingFirmwareView, setEditingFirmwareView] = useState(null);

    // functions
    let loadFirmwareViews = async ()=> {
        setLoading(true);
        let firmwareViews = (await firmwareApi.getAllFirmwareViews()).data.data;
        for(let firmwareView of firmwareViews) {
            firmwareView.children =firmwareView.firmwares;
        }
        setFirmwareViews(firmwareViews);
        setLoading(false);
    };

    // event
    let onAddFirmwareClick = ()=> {
        setOpenCreateFirmwareModal(true);
    };

    let onViewFirmwareDetails = (firmwareView)=> {
        setOpenCreateFirmwareModal(true);
        setEditingFirmwareView(firmwareView);
    };

    let onDeleteFirmware = async (firmwareView)=> {
        await firmwareApi.delete(firmwareView.id);
        messageApi.open({
            type: 'success',
            content: '删除固件成功',
        });
        loadFirmwareViews();
    };

    let onFirmwareWizardClose = ()=> {
        setOpenCreateFirmwareModal(false);
        setEditingFirmwareView(null);
    };

    let onFirmwareWizardComplete = ()=> {
        onFirmwareWizardClose();
        loadFirmwareViews();
    };

    // effect
    useEffect(()=> {
        loadFirmwareViews();
    }, []);

    // render
    const firmwareColumns: TableColumnsType<any> = [
        {
            title: '产品型号',
            dataIndex: 'productModelName',
            key: 'productModelName',
            width: '150px',
            fixed: 'left',
            render: (text, record, index)=> {
                return <span>{record.productModelName || record.productModel}</span>
            }
        },
        {
            title: '最新版本号/版本号',
            dataIndex: 'version',
            key: 'version',
            align: 'center',
            width: '150px',
            fixed: 'left',
            render: (text, record, index)=> {
                return <span>{record.latestVersion || record.version}</span>
            }
        },
        {
            title: '固件大小 (KB)',
            dataIndex: 'size',
            key: 'size',
            align: 'center',
            width: '100px',
            render: (text, record, index)=> {
                if (record.size == null) {
                    return StringUtils.emptyToSlash('');
                }
                return (record.size / 1024).toFixed(1) + ' KB'
            }
        },
        {
            title: 'MD5校验',
            dataIndex: 'md5',
            key: 'md5',
            align: 'center',
            width: '150px',
            render: (text, record, index)=> {
                if (record.md5 == null) {
                    return StringUtils.emptyToSlash('');
                }
                return <span style={{display: 'inline-block', width: '300px', overflow: 'hidden', textOverflow: 'ellipsis'}}>{record.md5}</span>;
            }
        },
        {
            title: '备注',
            dataIndex: 'remarks',
            key: 'remarks',
            width: '300px',
            render: (text, record, index)=> {
                return <span style={{display: 'inline-block', width: '300px', overflow: 'hidden', textOverflow: 'ellipsis'}}>{record.remarks}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            fixed: 'right',
            render: (text, record, index)=> {
                return record.id && <Space size="middle">
                    <a href={Api.URL_PREFIX + '/firmware/download/' + record.id}>下载固件</a>
                    <a onClick={()=> onViewFirmwareDetails(record)}>查看详情</a>
                    <Popconfirm
                            title="删除固件"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>此操作将删除固件，确认要删除么?</div> }
                            onConfirm={()=> onDeleteFirmware(record)}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <a>删除固件</a>
                    </Popconfirm>
                </Space>
            }
        }
    ];

    return (
        <FirmwareManagementWrapper>
            {contextHolder}
            <ToolBarWrapper>
                <ToolBarLeftWrapper>
                    <Space size="middle">
                        <Button type="primary" icon={<PlusOutlined />} onClick={onAddFirmwareClick}>新增固件</Button>
                    </Space>
                </ToolBarLeftWrapper>

                <ToolBarRightWrapper>
                    <Input 
                        size="middle"
                        placeholder="请输入关键字" 
                        suffix={<SearchOutlined />} 
                    />
                </ToolBarRightWrapper>

                <Modal open={openCreateFirmwareModal} 
                    onClose={()=> setOpenCreateFirmwareModal(false)} 
                    footer={null} 
                    closable={false}
                    destroyOnClose={true}
                    width={1000}
                    >
                    <CreateFirmwareWizard 
                        onClose={onFirmwareWizardClose} 
                        onComplete={onFirmwareWizardComplete}
                        firmware={editingFirmwareView}
                    />
                </Modal>
            </ToolBarWrapper>

            <TableWrapper>
                <Table 
                    loading={loading}
                    rowKey={(record)=> record.productModel + record.version}
                    columns={firmwareColumns} 
                    dataSource={firmwareViews}
                    scroll={{x: 'max-content'}}
                />
            </TableWrapper>
        </FirmwareManagementWrapper>
    );
};

export default FirmwareManagement;
